/**
 * Safely count tracks stored in a SavedPlaylistSql.tracks field.
 *
 * The `tracks` field has been stored in multiple formats over time:
 * - JSON string array: `["20518","22468"]`
 * - JSON number array: `[115, 22282, 8052]`
 * - Already-parsed JS array (when Knex returns jsonb columns, or when the
 *   value passes through certain server code paths): `[115, 22282]`
 * - Postgres array literal: `"{115,22282,8052}"`
 * - Comma-separated string: `"115,22282,8052"`
 * - null / undefined / empty string
 *
 * If parsing fails, falls back to the `track_count` column (added by
 * migration 006) if the caller provides it.
 *
 * @param {string | string[] | number[] | undefined | null} tracks
 * @param {number | undefined | null} [trackCountFallback] - DB `track_count` column
 * @returns {number}
 */
export default function parseTrackCount(tracks, trackCountFallback) {
  if (tracks == null) {
    return typeof trackCountFallback === "number" ? trackCountFallback : 0;
  }

  // Already-parsed JS array (happens with jsonb columns or server-side
  // transformations — parseTracksHack in musicHelpers.ts handles this case
  // too, which confirms the server emits arrays in some code paths).
  if (Array.isArray(tracks)) return tracks.length;

  if (typeof tracks !== "string" || tracks.length === 0) {
    return typeof trackCountFallback === "number" ? trackCountFallback : 0;
  }

  // Postgres array literal: {115,22282,8052}
  if (tracks.startsWith("{") && tracks.endsWith("}")) {
    const inner = tracks.slice(1, -1).trim();
    if (inner.length === 0) return 0;
    return inner.split(",").filter((s) => s.trim().length > 0).length;
  }

  // Try JSON parse first (covers both `["20518"]` and `[115, 22282]`).
  try {
    const parsed = JSON.parse(tracks);
    if (Array.isArray(parsed)) return parsed.length;
    // JSON but not an array — fall through to fallback.
  } catch {
    // Not JSON — fall through to comma-split below.
  }

  // Comma-separated string: "115,22282,8052"
  const commaCount = tracks
    .split(",")
    .filter((s) => s.trim().length > 0).length;
  if (commaCount > 0) return commaCount;

  // Everything failed — use the DB track_count column if available.
  return typeof trackCountFallback === "number" ? trackCountFallback : 0;
}
